import { useQuery } from '@tanstack/react-query'
import type { Hotel } from '~/hooks/type'
const API_BASE_URL = process.env.NEXT_PUBLIC_INV_URL
interface ApiResponse<T> {
  data: T
  isSuccess: boolean
}

const fetcher = async (url: string): Promise<Hotel[]> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  const result = (await response.json()) as ApiResponse<Hotel[]>
  return result.data
}

export function useHotels() {
  const { data, error, isLoading } = useQuery<Hotel[], Error>({
    queryKey: ['hotels'],
    queryFn: () => fetcher(`${API_BASE_URL}/cm/hotel/public/list`),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  return {
    hotels: data || [],
    isLoading,
    isError: error,
  }
}

export function useHotelByHotelId(hotelId?: string | null) {
  const { hotels, isLoading, isError } = useHotels()
  const hotel = hotels.find((h) => h.hotelId === hotelId)

  return {
    hotel,
    isLoading,
    isError,
  }
}

export function useSearchHotels(params?: {
  location?: string
  checkIn?: string
  checkOut?: string
  guests?: number
  minPrice?: number
  maxPrice?: number
}) {
  const queryParams = new URLSearchParams()

  if (params) {
    if (params.location) queryParams.append('location', params.location)
    if (params.checkIn) queryParams.append('checkIn', params.checkIn)
    if (params.checkOut) queryParams.append('checkOut', params.checkOut)
    if (params.guests) queryParams.append('guests', params.guests.toString())
    if (params.minPrice) queryParams.append('minPrice', params.minPrice.toString())
    if (params.maxPrice) queryParams.append('maxPrice', params.maxPrice.toString())
  }

  const queryString = queryParams.toString()
  const url = `${API_BASE_URL}/cm/hotel/public/search${queryString ? `?${queryString}` : ''}`

  const { data, error, isLoading } = useQuery<Hotel[], Error>({
    queryKey: ['searchHotels', params],
    queryFn: () => fetcher(url),
    enabled: !!params,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  return {
    hotels: data || [],
    isLoading,
    isError: error,
  }
}
